import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatIDR, formatDateTime, formatDuration, liveElapsed, relativeTime, overtimeSeconds, formatSecs } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Play, StopCircle, Trash2, Gamepad2, Pencil, Tv, Power, Wifi, Info, AlarmClock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const typeColors = {
  'PS5': 'text-cyan-400 border-cyan-500/50 bg-cyan-500/10',
  'PS4': 'text-fuchsia-400 border-fuchsia-500/50 bg-fuchsia-500/10',
  'PS3': 'text-amber-400 border-amber-500/50 bg-amber-500/10',
  'PS VR': 'text-violet-400 border-violet-500/50 bg-violet-500/10',
};

export default function PSRental() {
  const [units, setUnits] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [config, setConfig] = useState({ agent_configured: false });
  const [, setTick] = useState(0);
  const [unitModal, setUnitModal] = useState({ open: false, editing: null });
  const [startModal, setStartModal] = useState({ open: false, unit: null });
  const [extendModal, setExtendModal] = useState({ open: false, rental: null });

  const load = async () => {
    const [u, r] = await Promise.all([api.listUnits(), api.listRentals('active')]);
    setUnits(u);
    setRentals(r);
  };

  useEffect(() => {
    load();
    api.getConfig().then(setConfig).catch(() => {});
    const dataTimer = setInterval(load, 8000);
    const uiTimer = setInterval(() => setTick(x => x + 1), 1000);
    return () => { clearInterval(dataTimer); clearInterval(uiTimer); };
  }, []);

  const getActive = (unitId) => rentals.find(r => r.unit_id === unitId);

  const handleStop = async (rental) => {
    try {
      const result = await api.stopRental(rental.id);
      if (result.prepaid_total_price != null) {
        toast.success(`Sesi selesai · Tarif prepaid ${formatIDR(result.total_price)}`);
      } else {
        toast.success(`Sesi selesai · ${formatIDR(result.total_price)}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghentikan sesi');
    }
  };

  const handleDeleteUnit = async (unit) => {
    if (!window.confirm(`Hapus unit "${unit.name}"?`)) return;
    try {
      await api.deleteUnit(unit.id);
      toast.success('Unit dihapus');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const handleTvToggle = async (unit) => {
    try {
      const res = await api.tvPower(unit.id, 'toggle');
      toast.success(`TV ${res.tv_power ? 'ON' : 'OFF'} · ${unit.name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal kirim perintah TV');
    }
  };

  return (
    <div className="space-y-6" data-testid="rental-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Rental PlayStation</h2>
          <p className="text-slate-400 text-sm mt-1">Kelola unit, mulai sesi baru, dan pantau durasi bermain secara real-time.</p>
        </div>
        <Button
          data-testid="btn-add-unit-modal"
          onClick={() => setUnitModal({ open: true, editing: null })}
          className="bg-cyan-500 hover:bg-cyan-400 text-[#090D16] font-semibold gap-2 shadow-[0_0_15px_rgba(0,240,255,0.3)]"
        >
          <Plus className="w-4 h-4" /> Tambah Unit
        </Button>
      </div>

      {/* TV Control Info Banner */}
      {config.agent_configured ? (
        <div data-testid="tv-live-banner" className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 sm:p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            <span className="font-semibold text-emerald-400 font-mono-tactical uppercase tracking-widest text-[10px] mr-2">TV Control · Live Mode</span>
            Local relay agent aktif. Tombol Power akan menyalakan/mematikan Android TV via <span className="font-mono-tactical text-cyan-400">WoL &amp; ADB</span>. Status <span className="text-emerald-400">ONLINE</span>/<span className="text-amber-400">BOOTING</span>/<span className="text-red-400">TAK TERJANGKAU</span> di-update tiap 30 detik.
          </div>
        </div>
      ) : (
        <div data-testid="tv-mock-banner" className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 sm:p-4 flex items-start gap-3">
          <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            <span className="font-semibold text-amber-400 font-mono-tactical uppercase tracking-widest text-[10px] mr-2">TV Control · Mock Mode</span>
            Status on/off TV disimulasikan (tersimpan di database, otomatis ON saat sesi mulai, OFF saat sesi stop). Untuk kontrol nyata Android TV: baca <span className="font-mono-tactical text-cyan-400">/app/local_agent/README.md</span>, jalankan agent di PC warnet, expose via Cloudflare Tunnel, lalu set <span className="font-mono-tactical text-cyan-400">AGENT_WEBHOOK_URL</span> di backend .env.
          </div>
        </div>
      )}

      {/* Units Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5">
        {units.length === 0 && (
          <div className="col-span-full rounded-xl border border-dashed border-slate-700 p-8 text-center text-slate-500">
            Belum ada unit. Klik &quot;Tambah Unit&quot; untuk memulai.
          </div>
        )}
        {units.map(u => {
          const active = getActive(u.id);
          const isActive = !!active;
          return (
            <div
              key={u.id}
              data-testid={`unit-card-${u.id}`}
              className={`rounded-xl bg-[#131B2E] p-5 transition-all ${isActive ? 'neon-lime-card animate-pulse-glow' : 'neon-cyan-card'}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Gamepad2 className="w-4 h-4 text-slate-300" />
                  <span className={`text-[10px] font-mono-tactical uppercase tracking-widest px-2 py-0.5 rounded border ${typeColors[u.type] || 'text-slate-400 border-slate-600'}`}>
                    {u.type}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`btn-edit-unit-${u.id}`}
                    onClick={() => setUnitModal({ open: true, editing: u })}
                    className="p-1.5 rounded text-slate-500 hover:text-cyan-400 hover:bg-slate-800"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`btn-delete-unit-${u.id}`}
                    onClick={() => handleDeleteUnit(u)}
                    disabled={isActive}
                    className="p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <h4 className="font-display font-semibold text-lg text-slate-100">{u.name}</h4>
              <p className="text-xs text-slate-400 mt-0.5">Tarif <span className="text-cyan-400 font-mono-tactical">{formatIDR(u.hourly_rate)}</span>/jam</p>

              {/* TV Status + Power */}
              <TVStatusRow unit={u} onToggle={() => handleTvToggle(u)} agentConfigured={config.agent_configured} />

              <div className="mt-4 pt-4 border-t border-slate-800">
                {isActive ? (
                  <ActiveSessionBlock
                    unit={u}
                    active={active}
                    onStop={() => handleStop(active)}
                    onExtend={() => setExtendModal({ open: true, rental: active })}
                  />
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="w-2 h-2 rounded-full bg-slate-600" />
                      <span className="font-mono-tactical uppercase tracking-widest">Tersedia</span>
                    </div>
                    <Button
                      data-testid={`btn-start-rental-${u.id}`}
                      onClick={() => setStartModal({ open: true, unit: u })}
                      className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#090D16] font-semibold gap-2"
                    >
                      <Play className="w-4 h-4" /> Mulai Sesi
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <UnitFormDialog
        state={unitModal}
        onClose={() => setUnitModal({ open: false, editing: null })}
        onSaved={load}
      />
      <StartRentalDialog
        state={startModal}
        onClose={() => setStartModal({ open: false, unit: null })}
        onStarted={load}
      />
      <ExtendRentalDialog
        state={extendModal}
        onClose={() => setExtendModal({ open: false, rental: null })}
        onExtended={load}
      />
    </div>
  );
}

function ActiveSessionBlock({ unit: u, active, onStop, onExtend }) {
  const isPrepaid = active.prepaid_total_price != null;
  const overtime = isPrepaid && active.end_time ? overtimeSeconds(active.end_time) : 0;
  const isOvertime = overtime > 0;

  return (
    <div className="space-y-3">
      {isPrepaid && !isOvertime && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-2.5 py-1.5 flex items-center justify-between" data-testid={`prepaid-badge-${u.id}`}>
          <span className="text-[10px] font-mono-tactical uppercase tracking-widest text-emerald-400">
            Prepaid · {formatDuration(active.duration_hours)}
            {active.bonus_hours > 0 && <span className="text-violet-400"> + Bonus {formatDuration(active.bonus_hours)}</span>}
          </span>
          <span className="text-xs font-mono-tactical font-bold text-emerald-400">{formatIDR(active.prepaid_total_price)}</span>
        </div>
      )}

      {isOvertime && (
        <div className="rounded-md border border-red-500/60 bg-red-500/10 px-2.5 py-2 animate-pulse" data-testid={`overtime-warning-${u.id}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
            <span className="text-[10px] font-mono-tactical uppercase tracking-widest text-red-400">Waktu Habis · Overtime</span>
          </div>
          <p className="text-lg font-mono-tactical font-bold text-red-400 mt-1" data-testid={`overtime-timer-${u.id}`}>
            +{formatSecs(overtime)}
          </p>
        </div>
      )}

      <div>
        <p className="text-[10px] font-mono-tactical uppercase tracking-widest text-slate-500">
          {isPrepaid ? (isOvertime ? 'Durasi Total Berjalan' : 'Durasi Berjalan') : 'Durasi Berjalan'}
        </p>
        <p className={`font-mono-tactical text-2xl font-bold ${isOvertime ? 'text-red-400' : 'text-emerald-400'}`} data-testid={`timer-unit-${u.id}`}>
          {liveElapsed(active.start_time)}
        </p>
      </div>

      {active.customer_name && (
        <p className="text-xs text-slate-400 truncate">Pelanggan: <span className="text-slate-200">{active.customer_name}</span></p>
      )}

      <div className="flex gap-2">
        {isPrepaid && (
          <Button
            data-testid={`btn-extend-rental-${u.id}`}
            onClick={onExtend}
            variant="outline"
            className="flex-1 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 font-semibold gap-1.5"
          >
            <AlarmClock className="w-4 h-4" /> Perpanjang
          </Button>
        )}
        <Button
          data-testid={`btn-stop-rental-${u.id}`}
          onClick={onStop}
          className="flex-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold gap-2"
        >
          <StopCircle className="w-4 h-4" /> Stop
        </Button>
      </div>
    </div>
  );
}

function TVStatusRow({ unit, onToggle, agentConfigured }) {
  const hasHeartbeat = !!unit.tv_last_ping_at;
  let statusLabel = unit.tv_power ? 'ON' : 'OFF';
  let statusColor = unit.tv_power ? 'text-emerald-400' : 'text-slate-400';
  let iconColor = unit.tv_power ? 'text-emerald-400' : 'text-slate-500';

  if (agentConfigured && hasHeartbeat && unit.tv_power) {
    if (unit.tv_reachable) {
      statusLabel = 'ONLINE';
      statusColor = 'text-emerald-400';
      iconColor = 'text-emerald-400';
    } else {
      const secsSinceOn = unit.tv_last_action_at
        ? (Date.now() - new Date(unit.tv_last_action_at).getTime()) / 1000
        : 999;
      if (secsSinceOn < 60) {
        statusLabel = 'BOOTING';
        statusColor = 'text-amber-400';
        iconColor = 'text-amber-400';
      } else {
        statusLabel = 'TAK TERJANGKAU';
        statusColor = 'text-red-400';
        iconColor = 'text-red-400';
      }
    }
  }

  return (
    <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-[#0E1424] border border-slate-800 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <Tv className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
        <div className="min-w-0">
          <p className="text-[10px] font-mono-tactical uppercase tracking-widest text-slate-500">Android TV</p>
          <p className={`text-xs font-mono-tactical font-semibold truncate ${statusColor}`} data-testid={`tv-status-${unit.id}`}>
            {statusLabel}
            {unit.tv_ip && <span className="text-slate-500 ml-1">· {unit.tv_ip}</span>}
          </p>
          {agentConfigured && unit.tv_last_ping_at && (
            <p className="text-[10px] text-slate-600 truncate" data-testid={`tv-ping-${unit.id}`}>
              ping {relativeTime(unit.tv_last_ping_at)}
            </p>
          )}
        </div>
      </div>
      <button
        data-testid={`btn-tv-power-${unit.id}`}
        onClick={onToggle}
        title={unit.tv_power ? 'Matikan TV' : 'Hidupkan TV'}
        className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
          unit.tv_power
            ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-400/50 hover:bg-emerald-500/25'
            : 'bg-slate-800/60 text-slate-400 border border-slate-700 hover:bg-slate-700 hover:text-cyan-300'
        }`}
      >
        <Power className="w-4 h-4" />
      </button>
    </div>
  );
}

function UnitFormDialog({ state, onClose, onSaved }) {
  const editing = state.editing;
  const [name, setName] = useState('');
  const [type, setType] = useState('PS5');
  const [rate, setRate] = useState('');
  const [tvIp, setTvIp] = useState('');
  const [tvMac, setTvMac] = useState('');
  const [tvSsid, setTvSsid] = useState('');

  useEffect(() => {
    if (state.open) {
      setName(editing?.name || '');
      setType(editing?.type || 'PS5');
      setRate(editing?.hourly_rate?.toString() || '');
      setTvIp(editing?.tv_ip || '');
      setTvMac(editing?.tv_mac || '');
      setTvSsid(editing?.tv_wifi_ssid || '');
    }
  }, [state.open, editing]);

  const submit = async () => {
    if (!name.trim() || !rate) {
      toast.error('Nama dan tarif wajib diisi');
      return;
    }
    try {
      const payload = {
        name: name.trim(),
        type,
        hourly_rate: Number(rate),
        tv_ip: tvIp.trim() || null,
        tv_mac: tvMac.trim() || null,
        tv_wifi_ssid: tvSsid.trim() || null,
      };
      if (editing) {
        await api.updateUnit(editing.id, payload);
        toast.success('Unit diperbarui');
      } else {
        await api.createUnit(payload);
        toast.success('Unit ditambahkan');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-cyan-500/30 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? 'Edit Unit PS' : 'Tambah Unit PS'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300">Nama Unit</Label>
            <Input
              data-testid="input-unit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="misal: PS5 VIP Room 1"
              className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-slate-300">Tipe</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="select-unit-type" className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#131B2E] border-slate-700 text-slate-100">
                <SelectItem value="PS3">PS3</SelectItem>
                <SelectItem value="PS4">PS4</SelectItem>
                <SelectItem value="PS5">PS5</SelectItem>
                <SelectItem value="PS VR">PS VR</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300">Tarif per Jam (Rp)</Label>
            <Input
              data-testid="input-unit-rate"
              type="number"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="20000"
              className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical"
            />
          </div>

          <div className="pt-3 mt-3 border-t border-slate-800">
            <div className="flex items-center gap-2 mb-3">
              <Wifi className="w-3.5 h-3.5 text-cyan-400" />
              <p className="text-[10px] font-mono-tactical uppercase tracking-widest text-slate-400">Android TV (opsional)</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 text-xs">IP TV</Label>
                <Input
                  data-testid="input-tv-ip"
                  value={tvIp}
                  onChange={(e) => setTvIp(e.target.value)}
                  placeholder="192.168.1.20"
                  className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical text-sm"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">MAC TV</Label>
                <Input
                  data-testid="input-tv-mac"
                  value={tvMac}
                  onChange={(e) => setTvMac(e.target.value)}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical text-sm uppercase"
                />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-slate-300 text-xs">SSID WiFi (untuk pelanggan)</Label>
              <Input
                data-testid="input-tv-ssid"
                value={tvSsid}
                onChange={(e) => setTvSsid(e.target.value)}
                placeholder="misal: Warnet-Gaming-5G"
                className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
              IP &amp; MAC dipakai local relay agent untuk kirim WoL/ADB ke Android TV. Kosongkan jika belum siap.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Batal</Button>
          <Button data-testid="btn-save-unit" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-[#090D16] font-semibold">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExtendRentalDialog({ state, onClose, onExtended }) {
  const rental = state.rental;
  const [extra, setExtra] = useState('1');

  useEffect(() => { if (state.open) setExtra('1'); }, [state.open]);

  const extraPrice = rental && extra ? Number(extra) * rental.hourly_rate : 0;

  const submit = async () => {
    if (!rental || !extra || Number(extra) <= 0) {
      toast.error('Masukkan jam tambahan');
      return;
    }
    try {
      const result = await api.extendRental(rental.id, Number(extra));
      toast.success(`Diperpanjang ${extra} jam · Total ${formatIDR(result.prepaid_total_price)}`);
      onExtended();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memperpanjang sesi');
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-amber-500/40 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlarmClock className="w-5 h-5 text-amber-400" /> Perpanjang Sesi
          </DialogTitle>
        </DialogHeader>
        {rental && (
          <>
            <div className="rounded-lg bg-[#0E1424] border border-slate-800 p-3 mb-2">
              <p className="font-semibold text-slate-100">{rental.unit_name}</p>
              <p className="text-xs text-slate-400 mt-0.5">
                Tarif: <span className="text-cyan-400 font-mono-tactical">{formatIDR(rental.hourly_rate)}/jam</span>
                {' · '}Sudah dibayar: <span className="text-emerald-400 font-mono-tactical">{formatIDR(rental.prepaid_total_price)}</span>
                {' ('}{formatDuration(rental.duration_hours)}{')'}
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Jam Tambahan</Label>
              <Input
                data-testid="input-extend-hours"
                type="number"
                step="0.5"
                min="0.5"
                value={extra}
                onChange={(e) => setExtra(e.target.value)}
                className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical"
              />
            </div>
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/40 p-3 mt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Biaya tambahan</span>
                <span className="font-mono-tactical font-semibold text-amber-400" data-testid="text-extend-extra">{formatIDR(extraPrice)}</span>
              </div>
              <div className="flex justify-between text-sm pt-1 border-t border-amber-500/20">
                <span className="text-slate-300 font-medium">Total baru</span>
                <span className="font-mono-tactical font-bold text-emerald-400" data-testid="text-extend-new-total">{formatIDR((rental.prepaid_total_price || 0) + extraPrice)}</span>
              </div>
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Batal</Button>
          <Button data-testid="btn-confirm-extend" onClick={submit} className="bg-amber-500 hover:bg-amber-400 text-[#090D16] font-semibold gap-2">
            <AlarmClock className="w-4 h-4" /> Perpanjang
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StartRentalDialog({ state, onClose, onStarted }) {
  const unit = state.unit;
  const [customerName, setCustomerName] = useState('');
  const [duration, setDuration] = useState('');
  const [bonus, setBonus] = useState('');

  useEffect(() => {
    if (state.open) {
      setCustomerName('');
      setDuration('');
      setBonus('');
    }
  }, [state.open]);

  const estimatedPrice = () => {
    const d = Number(duration);
    if (!unit || !d) return null;
    return d * unit.hourly_rate;
  };

  const totalPlaytime = () => {
    const d = Number(duration) || 0;
    const b = Number(bonus) || 0;
    if (!d) return null;
    return d + b;
  };

  const submit = async () => {
    if (!unit) return;
    try {
      const payload = { unit_id: unit.id };
      if (customerName.trim()) payload.customer_name = customerName.trim();
      if (duration) payload.duration_hours = Number(duration);
      if (duration && bonus && Number(bonus) > 0) payload.bonus_hours = Number(bonus);
      await api.startRental(payload);
      toast.success('Sesi rental dimulai');
      onStarted();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal memulai sesi');
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-emerald-500/40 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-display">Mulai Sesi Rental</DialogTitle>
        </DialogHeader>
        {unit && (
          <>
            <div className="rounded-lg bg-[#0E1424] border border-slate-800 p-3 mb-2">
              <p className="text-xs font-mono-tactical uppercase tracking-widest text-slate-500">Unit</p>
              <p className="font-semibold text-slate-100">{unit.name} · <span className="text-cyan-400">{formatIDR(unit.hourly_rate)}/jam</span></p>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-300">Nama Pelanggan (opsional)</Label>
                <Input
                  data-testid="input-customer-name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="misal: Rian"
                  className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Durasi Bayar (jam) — kosongkan untuk open-ended</Label>
                <Input
                  data-testid="input-rental-duration-hours"
                  type="number"
                  step="0.5"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="2"
                  className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical"
                />
              </div>
              <div>
                <Label className="text-slate-300">Bonus Waktu Gratis (jam) — tidak dihitung omset</Label>
                <Input
                  data-testid="input-bonus-hours"
                  type="number"
                  step="0.25"
                  min="0"
                  value={bonus}
                  onChange={(e) => setBonus(e.target.value)}
                  placeholder="0.5"
                  className="bg-[#0E1424] border-slate-700 text-slate-100 mt-1 font-mono-tactical"
                  disabled={!duration}
                />
                <p className="text-[10px] text-slate-500 mt-1">Contoh paket: bayar 2 jam + bonus 0.5 jam → main 2.5 jam, omset tetap 2 jam.</p>
              </div>
              {estimatedPrice() != null && (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 p-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Total durasi main</span>
                    <span className="font-mono-tactical text-slate-200" data-testid="text-total-playtime">{formatDuration(totalPlaytime())}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-1 border-t border-emerald-500/20">
                    <span className="text-slate-300 font-medium">Harga (hanya jam bayar)</span>
                    <span className="text-emerald-400 font-mono-tactical font-bold" data-testid="text-rental-estimate">{formatIDR(estimatedPrice())}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-300">Batal</Button>
          <Button data-testid="btn-confirm-start-rental" onClick={submit} className="bg-emerald-500 hover:bg-emerald-400 text-[#090D16] font-semibold gap-2">
            <Play className="w-4 h-4" /> Mulai
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
